import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { FaArrowLeft } from 'react-icons/fa';
import { AudioContext } from '../context/AudioContext';
import TrackItem from './TrackItem';
import '../styles/OtherUserTopTracks.css';

const OtherUserTopTracks = () => {
  const { id: userId } = useParams();
  const navigate = useNavigate();
  const { handleTrackClick } = useContext(AudioContext);
  const [topTracks, setTopTracks] = useState([]);
  const [pinnedTracks, setPinnedTracks] = useState([]);
  const [userData, setUserData] = useState({
    artistName: '',
    profilePicture: '',
  });
  const [loading, setLoading] = useState(true);
  const auth = getAuth();

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) {
        setLoading(false);
        return;
      }

      try {
        const userResponse = await axios.get(
          `https://sync-api-troz.onrender.com/api/users/firebase/${userId}`
        );
        setUserData(userResponse.data);

        const pinnedResponse = await axios.get(
          `https://sync-api-troz.onrender.com/api/uploads/pinned`,
          { params: { firebaseUid: userId } }
        );
        setPinnedTracks(pinnedResponse.data);

        const uploadsResponse = await axios.get(
          `https://sync-api-troz.onrender.com/api/uploads`,
          { params: { firebaseUid: userId } }
        );

        const sortedTracks = uploadsResponse.data.sort(
          (a, b) => (b.plays || 0) - (a.plays || 0)
        );
        setTopTracks(sortedTracks);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching other user top tracks:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, [userId]);

  const handleLikeTrack = async (trackId, trackUserId) => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      window.alert('You must be logged in to like a track.');
      return;
    }

    if (trackUserId === currentUser.uid) {
      window.alert("You can't like your own track.");
      return;
    }

    try {
      const token = await currentUser.getIdToken();
      await axios.post(
        'https://sync-api-troz.onrender.com/api/uploads/like',
        { trackId, firebaseUid: currentUser.uid },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.alert('Track liked successfully!');
      setTopTracks((prev) =>
        prev.map((track) =>
          track._id === trackId
            ? { ...track, likes: (track.likes || 0) + 1, likedBy: [...(track.likedBy || []), currentUser.uid] }
            : track
        )
      );
    } catch (error) {
      console.error('Error liking track:', error.response?.data || error.message);
      window.alert(
        error.response?.data?.message === 'You have already liked this track'
          ? 'You have already liked this track.'
          : 'An error occurred while liking the track.'
      );
    }
  };

  const handleStarTrack = async (trackId) => {
    const firebaseUid = auth.currentUser?.uid;
    if (!firebaseUid) {
      window.alert('You must be logged in to star a track.');
      return;
    }

    try {
      await axios.post('https://sync-api-troz.onrender.com/api/uploads/star', {
        trackId,
        userId: firebaseUid,
      });
      window.alert('Track starred successfully!');
      setTopTracks((prev) =>
        prev.map((track) =>
          track._id === trackId
            ? {
                ...track,
                stars: (track.stars || 0) + 1,
                starredBy: [...(track.starredBy || []), firebaseUid],
              }
            : track
        )
      );
    } catch (error) {
      console.error('Error starring track:', error.response?.data || error.message);
      window.alert(
        error.response?.data?.message === 'You have already starred a track from this artist'
          ? 'You already starred a track from this user.'
          : 'An error occurred while starring the track.'
      );
    }
  };

  if (loading) {
    return (
      <div className="other-user-top-tracks-loading">
        <div className="loader"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="other-user-top-tracks-page">
      <div className="other-user-top-tracks-container">
        <div className="other-user-top-tracks-banner">
          {userData.profilePicture ? (
            <img
              src={userData.profilePicture}
              alt="Profile"
              className="other-user-top-tracks-banner-image"
            />
          ) : (
            <div className="other-user-top-tracks-banner-placeholder" />
          )}
          <p className="other-user-top-tracks-artist-name">{userData.artistName}</p>
        </div>

        <div className="other-user-top-tracks-header">
          <button onClick={() => navigate(-1)} className="other-user-top-tracks-back-button">
            <FaArrowLeft size={24} />
          </button>
          <p className="other-user-top-tracks-header-text">Top Tracks</p>
          <div className="other-user-top-tracks-placeholder" />
        </div>

        <div className="other-user-top-tracks-results">
          {topTracks.length > 0 ? (
            topTracks.map((track) => (
              <TrackItem
                key={track._id}
                track={track}
                onPlay={() => handleTrackClick(track)}
                onLike={() => handleLikeTrack(track._id, track.user?.firebaseUid)}
                onStar={() => handleStarTrack(track._id)}
                isOwner={false}
                showPinButton={false}
                showDeleteButton={false}
              />
            ))
          ) : (
            <p className="other-user-top-tracks-no-results">This user has no tracks yet.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default OtherUserTopTracks;